import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Bell, Plus, Trash2, Save } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

interface AlertSettingsProps {
  shopId: string;
}

interface AlertConfig {
  id?: string;
  metric_type: string;
  threshold_value: number;
  comparison_operator: string;
  enabled: boolean;
  notify_roles: string[];
}

export function AlertSettings({ shopId }: AlertSettingsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newAlert, setNewAlert] = useState<Partial<AlertConfig>>({
    metric_type: 'technician_efficiency',
    threshold_value: 85,
    comparison_operator: 'less_than',
    enabled: true,
    notify_roles: ['MANAGER']
  });

  const { data: configs, isLoading } = useQuery({
    queryKey: ['alert-configs', shopId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('alert_configurations')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (alert: Partial<AlertConfig>) => {
      const { error } = await supabase
        .from('alert_configurations')
        .insert([{
          shop_id: shopId,
          metric_type: alert.metric_type!,
          threshold_value: alert.threshold_value!,
          comparison_operator: alert.comparison_operator!,
          enabled: alert.enabled!,
          notify_roles: alert.notify_roles!
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-configs', shopId] });
      toast({
        title: "Alert created",
        description: "New alert configuration has been saved",
      });
      setNewAlert({
        metric_type: 'technician_efficiency',
        threshold_value: 85,
        comparison_operator: 'less_than',
        enabled: true,
        notify_roles: ['MANAGER']
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating alert",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('alert_configurations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-configs', shopId] });
      toast({
        title: "Alert deleted",
        description: "Alert configuration has been removed",
      });
    }
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from('alert_configurations')
        .update({ enabled })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-configs', shopId] });
    }
  });

  const getMetricLabel = (type: string) => {
    const labels: Record<string, string> = {
      'technician_efficiency': 'Technician Efficiency',
      'comeback_rate': 'Comeback Rate',
      'waiter_promise': 'Waiter Promise Rate',
      'job_performance': 'Job Performance'
    };
    return labels[type] || type;
  };

  const getOperatorLabel = (op: string) => {
    const labels: Record<string, string> = {
      'greater_than': 'Greater than',
      'less_than': 'Less than',
      'equals': 'Equals'
    };
    return labels[op] || op;
  };

  if (isLoading) {
    return <Card className="p-4"><div className="h-96 animate-pulse bg-muted rounded" /></Card>;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Bell className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-semibold text-foreground">Performance Alert Settings</h3>
      </div>

      {/* Existing Alerts */}
      <div className="space-y-3 mb-6">
        {configs?.map((config) => (
          <div key={config.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm text-foreground">
                  {getMetricLabel(config.metric_type)}
                </span>
                {config.enabled ? (
                  <Badge variant="default" className="text-xs">Active</Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Disabled</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Alert when {getOperatorLabel(config.comparison_operator).toLowerCase()} {config.threshold_value}%
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.enabled}
                onCheckedChange={(enabled) => toggleMutation.mutate({ id: config.id, enabled })}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteMutation.mutate(config.id)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        ))}

        {configs?.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No alerts configured yet. Create one below.
          </p>
        )}
      </div>

      {/* Create New Alert */}
      <div className="border-t border-border pt-6">
        <h4 className="font-medium text-sm text-foreground mb-4 flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create New Alert
        </h4>

        <div className="grid gap-4">
          <div>
            <Label htmlFor="metric_type">Metric Type</Label>
            <Select
              value={newAlert.metric_type}
              onValueChange={(value) => setNewAlert({ ...newAlert, metric_type: value })}
            >
              <SelectTrigger id="metric_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="technician_efficiency">Technician Efficiency</SelectItem>
                <SelectItem value="comeback_rate">Comeback Rate</SelectItem>
                <SelectItem value="waiter_promise">Waiter Promise Rate</SelectItem>
                <SelectItem value="job_performance">Job Performance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="operator">Condition</Label>
              <Select
                value={newAlert.comparison_operator}
                onValueChange={(value) => setNewAlert({ ...newAlert, comparison_operator: value })}
              >
                <SelectTrigger id="operator">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="less_than">Less than</SelectItem>
                  <SelectItem value="greater_than">Greater than</SelectItem>
                  <SelectItem value="equals">Equals</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="threshold">Threshold (%)</Label>
              <Input
                id="threshold"
                type="number"
                min="0"
                max="100"
                value={newAlert.threshold_value}
                onChange={(e) => setNewAlert({ ...newAlert, threshold_value: parseFloat(e.target.value) })}
              />
            </div>
          </div>

          <Button
            onClick={() => createMutation.mutate(newAlert)}
            disabled={createMutation.isPending}
            className="w-full"
          >
            <Save className="h-4 w-4 mr-2" />
            Create Alert
          </Button>
        </div>
      </div>
    </Card>
  );
}
